import { APITimeline } from '@cxc/ui-kit/chart.js/components/timeline';
import { Chart } from '@cxc/ui-kit/chart.js/models';

import { FxDisclosureWire } from '@models/API/fx-disclosure.model';
import { Insights } from '@models/operation/insights.model';
import { LenderPosition } from '@models/operation/lender-participation.model';
import { Operation as _Operation } from '@models/operation/operation.model';

export namespace Lender {
  export namespace Overview {
    export namespace Resume {
      export interface Operation {
        status: string;
        external_id: string;
        trust_number: string;
        alias: string;
        trustee: string;
        lenders: _Operation.Member[];
        originators: _Operation.Member[];
        currency: string;
        total_credit_amount: number;
        total_contracts: number;
        finished_operation_date?: string;
        created_at: string;
        updated_at: string;
        reported_at: string;
        master_admin?: string;
        charts: Chart.V1.Chart[];
      }
      export interface Response {
        lender: string;
        reported_at: string;
        core_metrics: Lender.CoreMetrics.Response;
        overview: APITimeline;
        operations: Operation[];
        carrousel_charts: Chart.V1.Chart[];
        first_chart: Chart.V1.Chart;
      }
    }

    export type Response = APITimeline;
  }

  /**
   * La cadencia con la que una facility reporta, inferida de sus propios
   * cortes (LEN-3280). Se declara acá y no se importa de `.api-docs` porque
   * este modelo es el escrito a mano: el generado no llega a `shell-mfe`.
   */
  export type CutOffPeriodicity =
    | 'Daily'
    | 'Weekly'
    | 'Biweekly'
    | 'Monthly'
    | 'Quarterly';

  export namespace CoreMetrics {
    /**
     * One group of a portfolio cut (LEN-2577). A null `key` groups the trusts
     * that did not report it. The server sends the groups in reading order:
     * positions in catalog order, servicers alphabetically, null last.
     */
    export interface BreakdownItem {
      key: string | null;
      trust_count: number;
      total_credit_amount: number;
      /** Null when no trust in the group reported a commitment. */
      committed_amount: number | null;
      /** `committed_amount` ÷ the portfolio's `committed_amount`, in percent. */
      share_pct: number | null;
    }

    export interface Response {
      reported_at: string;
      lender: string;
      interest_rate: number;
      outstanding_debt: number;
      total_contracts: number;
      operations: {
        trust_number: string;
        alias: string;
        interest_rate: number;
        outstanding_debt: number;
        total_contracts: number;
        total_credit_amount: number;
        currency: string;
        // LEN-2577, joined once per trust. Null when the trust did not report it.
        priority_position?: LenderPosition | null;
        master_admin?: string | null;
        committed_amount?: number | null;
        participation_pct?: number | null;
        /**
         * LEN-3279. La compañía dueña de la facility. `null` cuando no está
         * vinculada a ninguna, que es el caso que el aviso del CA15 cuenta.
         */
        company_id?: string | null;
        /**
         * LEN-3280. La cadencia con la que reporta, inferida de sus propios
         * cortes. `null` cuando no se puede inferir -- el front lo pinta como
         * "Reporting frequency not set", nunca adivina una.
         */
        reporting_periodicity?: CutOffPeriodicity | null;
      }[];
      total_credit_amount: number;
      currency: string;
      portfolio_balance: {
        elegible: number;
        inelegible: number;
        total: number;
      };
      pending_update_operations: string[];
      spread: number;
      // LEN-1809: present only when `outstanding_debt`/`portfolio_balance`
      // were actually converted - not `total_credit_amount` (see the
      // backend's own `LenderDetail.fx` docstring for why that one can't be
      // covered by a single disclosure).
      fx?: FxDisclosure;
      // LEN-2577: the lender's participation across the portfolio, always
      // served (the OpenAPI marks them optional only because they have
      // defaults). Trusts that did not report one count in
      // `total_credit_amount` and in the cuts, never in `committed_amount`,
      // which is null when none reported it.
      committed_amount: number | null;
      /** `committed_amount` ÷ `total_credit_amount`, in percent. */
      participation_pct: number | null;
      trusts_with_committed_amount: number;
      trusts_without_committed_amount: number;
      priority_position_breakdown: BreakdownItem[];
      master_servicer_breakdown: BreakdownItem[];
    }
  }

  /** The rate behind a converted figure, so an export can say where it came
   * from. An alias, not a second interface: both mirrored the backend's
   * `FxDisclosure` (`schemas/charts.py`) field by field, and two copies meant
   * a backend rename would update one and leave the compiler silent about the
   * other (MR !1207 S4). `FxDisclosureWire` is the copy that gets promoted to
   * `Chart.Base` when LEN-2142 brings the second consumer. */
  export type FxDisclosure = FxDisclosureWire;

  export namespace Matrix {
    export type Response = Insights.Matrix[];
  }

  export namespace Charts {
    export type Response = Chart.V1.Chart[];
  }

  export enum ChartName {
    TOTAL_CREDIT_AMOUNT = 'TOTAL_CREDIT_AMOUNT',
    AGING_SNAPSHOTS = 'AGING_SNAPSHOTS',

    COLLECTION_SNAPSHOTS = 'COLLECTION_SNAPSHOTS',
    ASSETS_SNAPSHOTS = 'ASSETS_SNAPSHOTS',
    BORROWINGS_SNAPSHOTS = 'BORROWINGS_SNAPSHOTS',
    OVERCOLLATERIZATIONS_SNAPSHOTS = 'OVERCOLLATERIZATIONS_SNAPSHOTS',
    PAYMENT_HISTORY = 'PAYMENT_HISTORY',
  }
}
